import React from "react";
import { ArrowRight, ArrowUpRight, Mic, Youtube, Play } from "lucide-react";
import { FIRM, SEMINARS } from "../data/content";
import { FALLBACK_VIDEOS, CHANNEL_UPLOADS_EMBED } from "../data/fallbackContent";
import { useConsultation } from "../components/ConsultationContext";
import Reveal from "../components/Reveal";
import SafeImage from "../components/SafeImage";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

export default function Media() {
  const consult = useConsultation();
  // Render with fallback videos immediately so the page never shows "Loading…"
  const [videos, setVideos] = React.useState(FALLBACK_VIDEOS);
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 7000);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/youtube/videos?limit=12`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.items) && d.items.length) setVideos(d.items);
      })
      .catch(() => { /* keep fallback content */ })
      .finally(() => clearTimeout(t));
  }, []);

  // featured is GUARANTEED to exist (videos has ≥1 item from FALLBACK_VIDEOS)
  const featured = videos[active] || videos[0];

  return (
    <div data-testid="media-page">
      <section className="bg-[#0A192F] text-white pt-44 pb-32 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[40rem] w-[40rem] rounded-full bg-[#C5A059]/8 blur-[140px]" aria-hidden />
        <div className="container-x relative">
          <p className="eyebrow text-[#C5A059]">Knowledge Hub</p>
          <h1 className="editorial-display text-[clamp(2.8rem,8.4vw,8.4rem)] mt-12 max-w-5xl">
            Thought leadership <br /><span className="italic text-[#C5A059]">and knowledge sharing.</span>
          </h1>
          <p className="text-white/65 mt-12 max-w-2xl text-lg lg:text-xl leading-[1.7]">
            CA. Nitta Ravi Kishore is a regular speaker at professional institutes and industry forums, addressing current and emerging issues in taxation and regulatory frameworks. He  also shares insights through his  YouTube channel <span className="italic text-[#C5A059]/90">CA. NRK</span>.
          </p>
        </div>
      </section>

      {/* Featured video */}
      <section className="bg-white">
        <div className="container-x py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <Reveal className="lg:col-span-8">
              <p className="eyebrow text-[#C5A059]">Featured · CA. NRK</p>
              <h2 className="editorial-display text-[#0A192F] text-[clamp(2.2rem,4.6vw,4.4rem)] mt-8 leading-[1.04] max-w-[24ch]">
                {featured?.title || "On the channel"}
              </h2>
              <div className="mt-12 aspect-video w-full bg-[#0A192F]">
                <iframe
                  data-testid="media-youtube-embed"
                  className="w-full h-full"
                  src={featured?.embed_url || CHANNEL_UPLOADS_EMBED}
                  title={featured?.title || "CA. NRK"}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              {featured?.link && (
                <a href={featured.link} target="_blank" rel="noopener noreferrer" data-testid="media-watch-on-youtube" className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mt-5 inline-flex items-center gap-2 hover:underline">
                  Watch on YouTube <ArrowUpRight size={12} />
                </a>
              )}
              {featured?.description && (
                <p className="text-[#475569] mt-8 leading-[1.85] max-w-[60ch]">{featured.description}</p>
              )}
              <a href={FIRM.contact.youtube} target="_blank" rel="noopener noreferrer" data-testid="media-youtube-link" className="btn-ghost-dark mt-10 inline-flex">
                <Youtube size={16} className="text-[#C5A059]" /> Visit channel <ArrowUpRight size={14} />
              </a>
            </Reveal>

            <Reveal className="lg:col-span-4" delay={150}>
              <p className="eyebrow text-[#C5A059]">Speaking profile</p>
              <h2 className="editorial-display text-[#0A192F] text-[clamp(1.9rem,3.4vw,3rem)] mt-8 leading-[1.05]">Selected engagements</h2>
              <ul className="mt-10 divide-y divide-[#0A192F]/10 border-t border-[#0A192F]/10">
                {SEMINARS.map((s, i) => (
                  <li key={i} data-testid={`seminar-${i}`} className="py-5 flex items-start gap-4">
                    <span className="num-tag mt-1">{String(i+1).padStart(2,"0")}</span>
                    <div className="flex-1">
                      <p className="text-[#0A192F] font-medium leading-snug text-[0.95rem]">{s.title}</p>
                      {s.handle && (s.href ? (
                        <a href={s.href} target="_blank" rel="noreferrer" data-testid={`seminar-link-${i}`} className="text-xs text-[#C5A059] mt-1.5 italic inline-flex items-center gap-1.5 hover:underline">
                          {s.handle} <ArrowUpRight size={11} />
                        </a>
                      ) : (
                        <p className="text-xs text-[#C5A059] mt-1.5 italic">{s.handle}</p>
                      ))}
                    </div>
                    <Mic size={14} className="text-[#C5A059] mt-1.5 flex-shrink-0" />
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Video library */}
      <section className="bg-[#FBFAF6]">
        <div className="container-x py-24 lg:py-32">
          <div className="mb-16">
            <p className="eyebrow text-[#C5A059]">Video Library</p>
            <h2 className="editorial-display text-[#0A192F] text-[clamp(2.4rem,5vw,5rem)] mt-10 leading-[1.0]">From the channel.</h2>
          </div>

          {videos.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 60}>
                  <button type="button" onClick={() => { setActive(i); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`video-${v.id}`} className="group text-left w-full">
                    <div className="relative overflow-hidden aspect-video bg-[#0A192F]">
                      <SafeImage src={v.thumbnail} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition duration-[1200ms]" loading="lazy" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white/95 text-[#0A192F] shadow-xl group-hover:bg-[#C5A059] transition-colors">
                          <Play size={20} className="ml-0.5" />
                        </span>
                      </div>
                    </div>
                    <p className="overline text-[#C5A059] text-[10px] mt-6">{fmtDate(v.published)}</p>
                    <h3 className="font-serif text-xl text-[#0A192F] mt-3 leading-[1.18] line-clamp-3 group-hover:text-[#C5A059] transition-colors">{v.title}</h3>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
